import { Location } from "@angular/common";
import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { AgPieSeriesTooltipRendererParams } from "ag-charts-community";
import { ColDef } from "@ag-grid-community/core";
import { Account } from "app/model/entities/entity-model";

import { DbQueryService } from "app/shared/db-query.service";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import _ from "lodash";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

interface ManufInventoryData {
  manufacturerId: number;
  manufacturerName: string;
  count: number;
}

@Component({
  selector: "app-chart-manuf-inventory",
  templateUrl: "./chart-manuf-inventory.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ChartManufInventoryComponent
  extends DomainBaseComponent
  implements OnInit
{
  @Input() account: Account;
  title: string;
  options: any;
  private chartData: ManufInventoryData[] = [];

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,

    protected location: Location,
    protected dbQueryService: DbQueryService
  ) {
    super(domainService);
  }

  async ngOnInit() {
    await this.processChartData();
    this.isPageReady = true;
  }

  //this will called when data is passed from parent to child.
  async ngOnChanges(val) {
    this.isPageReady = false;
    await this.processChartData();
    this.isPageReady = true;
  }

  private async processChartData() {
    // this.title = this.getTitle();
    let data = await this.dbQueryService.getTopManufacturersInInventory();
    const topCount = _.sum(data.map((d) => d.count));

    const totalCount = await this.dbQueryService.getItemCountInInventory();
    data = data.concat({
      manufacturerId: -1,
      manufacturerName: "All others",
      count: totalCount - topCount,
    });
    this.chartData = data as ManufInventoryData[];

    this.options = {
      title: {
        text: this.getTitle(),
      },
      data: data,
      series: [
        {
          type: "pie",
          angleKey: "count",
          calloutLabelKey: "manufacturerName",
          legendItemKey: "manufacturerName",
          labelName: "Manufacturer",
          tooltip: { renderer: this.renderer.bind(this) },
        },
      ],
    };
  }

  getTitle() {
    return `Inventory Counts by Manufacturer`;
  }

  renderer(params: AgPieSeriesTooltipRendererParams<any>) {
    return {
      title: params.datum.manufacturerName,
      content: params.datum.count + " Items",
    };
  }

  // ViewableWidget interface methods
  async exportToExcel(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Manufacturer Inventory");
    const exportDate = new Date();

    worksheet.addRow([this.getTitle()]);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells("A1:B1");

    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells("A2:B2");

    worksheet.addRow([]);

    worksheet.addRow(["Manufacturer", "Item Count"]);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };

    worksheet.getColumn(1).width = 30;
    worksheet.getColumn(2).width = 15;

    this.chartData.forEach((item) => {
      worksheet.addRow([item.manufacturerName, item.count]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Manufacturer_Inventory_${exportDate.toISOString().split("T")[0]}.xlsx`);
  }

  getGridData(): ManufInventoryData[] {
    return this.chartData;
  }

  getColumnDefs(): ColDef[] {
    return [
      { field: "manufacturerName", headerName: "Manufacturer", flex: 2 },
      { field: "count", headerName: "Item Count", flex: 1 },
    ];
  }

  getExportFileName(): string {
    return "Manufacturer_Inventory";
  }

  getDialogTitle(): string {
    return this.getTitle();
  }
}
